import * as React from 'react'

import { useCallback } from 'modules/core/hooks/useCallback'

import type Employee from 'modules/employees/store/models/Employee'
import type { AddPositionNodeData } from 'modules/org_chart/models/NodeData'
import type { PointNode } from 'modules/org_chart/models/PointNode'
import type { TagInfoProp } from 'modules/org_chart/models/TagInfoProp'

type SidePanelTabType = 'reports' | 'profile'

interface Context {
  hintedNode?: PointNode<AddPositionNodeData> | undefined
  activeEmployee?: Employee
  tagInfoProp: TagInfoProp
  searchResultId?: number
  activeSidePanelTab: SidePanelTabType
  update: (key: ChangeType, value: unknown) => void
}

type ChangeType = Omit<keyof Context, 'update'>

export const OrgChartStateContext = React.createContext<Context>({
  hintedNode: undefined,
  activeEmployee: undefined,
  tagInfoProp: 'workplace',
  searchResultId: undefined,
  activeSidePanelTab: 'profile',
  update: () => undefined,
})

type Props = React.PropsWithChildren<Record<string, unknown>>

const Provider: React.FC<Props> = ({ children }) => {
  const [hintedNode, setHintedNode] = React.useState<PointNode<AddPositionNodeData>>()
  const [activeEmployee, setActiveEmployee] = React.useState<Employee>()
  const [tagInfoProp, setTagInfoProp] = React.useState<TagInfoProp>('workplace')
  const [searchResultId, setSearchResultId] = React.useState<number>()
  const [activeSidePanelTab, setActiveSidePanelTab] = React.useState<SidePanelTabType>('profile')

  const update = useCallback((key: ChangeType, value: unknown): void => {
    switch (key) {
      case 'hintedNode':
        setHintedNode(value as PointNode<AddPositionNodeData>)
        break
      case 'activeEmployee':
        setActiveEmployee(value as Employee)
        break
      case 'tagInfoProp':
        setTagInfoProp(value as TagInfoProp)
        break
      case 'activeSidePanelTab':
        setActiveSidePanelTab(value as SidePanelTabType)
        break
      case 'searchResult':
        setSearchResultId(value as number)
      default:
        return
    }
  }, [])

  return (
    <OrgChartStateContext.Provider
      value={{
        hintedNode,
        activeEmployee,
        tagInfoProp,
        searchResultId,
        activeSidePanelTab,
        update,
      }}
    >
      {children}
    </OrgChartStateContext.Provider>
  )
}

export default Provider
